import { motion } from "framer-motion";
import { CLUB_CATALOG } from "../lib/clubs";

/**
 * Renders per-club dispersion ranges using p25/p50/p75 from real shot data.
 * Each row shows a horizontal bar (min..max) with a highlighted (p25..p75) band
 * and a marker at p50.
 */
export default function ShotDispersion({ clubs }) {
  if (!clubs || clubs.length === 0) return null;

  // Global min/max for shared x-axis scaling.
  const globalMin = Math.min(...clubs.map((c) => c.min));
  const globalMax = Math.max(...clubs.map((c) => c.max));
  const span = Math.max(1, globalMax - globalMin);
  const pos = (v) => ((v - globalMin) / span) * 100;
  const labelFor = (key) =>
    CLUB_CATALOG.find((c) => c.key === key)?.label || key.toUpperCase();

  return (
    <>
      <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-1">
        Shot dispersion
      </h3>
      <p className="text-xs text-[var(--text-secondary)] mb-3">
        From every shot you've logged. The shaded band is your typical 50% range
        (p25–p75), the dot is your median.
      </p>
      <div className="space-y-3 mb-5" data-testid="shot-dispersion">
        {clubs.map((c, i) => (
          <motion.div
            key={c.club_key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="surface-card-flat p-3"
            data-testid={`dispersion-${c.club_key}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-bold text-[var(--primary)] text-sm">
                {labelFor(c.club_key)}
              </div>
              <div className="text-xs text-[var(--text-secondary)] font-mono-num">
                p50 <span className="text-[var(--primary)] font-bold">{Math.round(c.p50)}</span>
                {" "}yds · {c.count} shots
              </div>
            </div>
            <div className="relative h-3 rounded-full bg-[var(--text-secondary)]/10 overflow-hidden">
              {/* min..max base line */}
              <div
                className="absolute h-0.5 top-1/2 -translate-y-1/2"
                style={{
                  left: `${pos(c.min)}%`,
                  width: `${pos(c.max) - pos(c.min)}%`,
                  background: "var(--text-secondary)",
                  opacity: 0.3,
                }}
              />
              {/* p25..p75 band */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.03 }}
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${pos(c.p25)}%`,
                  width: `${pos(c.p75) - pos(c.p25)}%`,
                  background: "var(--primary)",
                  opacity: 0.85,
                  borderRadius: 999,
                  transformOrigin: "left",
                }}
              />
              {/* p50 marker */}
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                style={{
                  left: `${pos(c.p50)}%`,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "var(--accent-terracotta, #C4543C)",
                  border: "2px solid #fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-[10px] font-mono-num text-[var(--text-secondary)]">
              <span>{Math.round(c.min)}</span>
              <span>{Math.round(c.max)} yds</span>
            </div>
          </motion.div>
        ))}
      </div>
    </>
  );
}
